import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import SupplierBasicInformation from './SupplierBasicInformation';
import SupplierBasicInformationForm from './SupplierBasicInformationForm';
import {
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from '../../thunks/suppliers';
import './styles.scss';

const { object, string, bool, func } = PropTypes;

class SupplierBasicInformationContainer extends Component {

  static propTypes = {
    supplierData: object,
    userId: string,
    orgKey: string,
    supplierKey: string,
    dispatch: func.isRequired,
    isLoading: bool,
    closeSupplierModal: func.isRequired,
  }

  static defaultProps = {
    supplierData: {},
    userId: null,
    orgKey: null,
    supplierKey: null,
    isLoading: false,
  }

  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      unlockedForEditing: false,
      confirmationModalIsOpen: false,
      infoModalIsOpen: false,
      infoModalText: '',
    };
  }

  requestDelete = () => {
    this.setState({ confirmationModalIsOpen: true });
  }

  edit = () => {
    this.setState({ unlockedForEditing: true });
  }

  cancelEdit = () => {
    this.setState({ unlockedForEditing: false });
  }

  cancelNew = () => {
    this.props.closeSupplierModal();
  }

  save = () => {
    this.setState({ submitting: true });
  }

  closeConfirmationModal = () => {
    this.setState({ confirmationModalIsOpen: false });
  }

  delete = async () => {
    await this.props.dispatch(deleteSupplier(this.props.supplierKey, 'disable only'));
    this.props.closeSupplierModal();
  }

  validate = (data) => {
    return data.companyName && data.address1 && data.address2 && data.city && data.county && data.postcode && data.logoUrl && data.mainTelephone && data.companyNumber && data.vatNumber && data.tier;
  }

  openInfoModal = (infoModalText) => {
    this.setState({
      infoModalIsOpen: true,
      infoModalText,
    });
  }

  closeInfoModal = () => {
    this.setState({
      infoModalIsOpen: false,
      infoModalText: '',
    });
  }

  saveData = async (supplierData) => {
    this.setState({
      unlockedForEditing: false,
      submitting: false,
    });
    const trimmedLogoUrl = typeof supplierData.logoUrl === 'string' ? supplierData.logoUrl.trim() : '';
    const supplierDataToSave = {
      ...supplierData,
      logoUrl: trimmedLogoUrl || 'N/A',
    };
    if (this.validate(supplierDataToSave)) {
      const { supplierKey, userId, orgKey } = this.props;
      if (supplierKey) {
        await this.props.dispatch(updateSupplier(supplierDataToSave, orgKey, userId, supplierKey));
      } else {
        await this.props.dispatch(createSupplier(supplierDataToSave, orgKey));
      }
    } else {
      this.openInfoModal('All fields are required');
    }
  }

  renderActions = (buttonState) => {
    if (buttonState === 'newSupplier') {
      return (
        <div className="supplier-workspace__actions">
          <Button white outline onClick={this.cancelNew}>Cancel</Button>
          <Button black onClick={this.save}>Create supplier</Button>
        </div>
      );
    }

    if (buttonState === 'editSupplier') {
      return (
        <div className="supplier-workspace__actions">
          <Button danger outline onClick={this.requestDelete}>Disable supplier</Button>
          <Button white outline onClick={this.cancelEdit}>Cancel</Button>
          <Button black onClick={this.save}>Save changes</Button>
        </div>
      );
    }

    return (
      <div className="supplier-workspace__actions">
        <Button black onClick={this.edit}>Edit supplier</Button>
      </div>
    );
  }

  render() {
    const { supplierKey, supplierData } = this.props;
    let buttonState;
    if (!supplierKey) {
      buttonState = 'newSupplier';
    } else if (this.state.unlockedForEditing) {
      buttonState = 'editSupplier';
    } else {
      buttonState = 'viewSupplier';
    }
    let workspaceTitle = 'Supplier details';
    if (buttonState === 'newSupplier') {
      workspaceTitle = 'Create supplier';
    } else if (buttonState === 'editSupplier') {
      workspaceTitle = 'Edit supplier details';
    }
    const workspaceDescription = buttonState === 'viewSupplier' ?
      'Review the organisation, address, registration, and brand information.' :
      'Logo URL is optional; all other fields are required.';

    return (
      <div className="supplier-workspace">
        {this.props.isLoading ?
          <div style={{ textAlign: 'center' }}>
            <Loading />
          </div>
          :
          <div>
            <header className="supplier-workspace__heading">
              <div>
                <h3 className="supplier-workspace__title">{workspaceTitle}</h3>
                <p className="supplier-workspace__description">{workspaceDescription}</p>
              </div>
              {buttonState === 'viewSupplier' && this.renderActions(buttonState)}
            </header>
            {!supplierKey || this.state.unlockedForEditing ?
              <SupplierBasicInformationForm
                initialValues={supplierData}
                onSubmit={this.saveData}
                triggerSubmit={this.state.submitting}
              /> :
              <SupplierBasicInformation
                supplierData={supplierData}
              />
            }
            {buttonState !== 'viewSupplier' &&
              <footer className="supplier-workspace__footer">
                {this.renderActions(buttonState)}
              </footer>
            }
            <Modal
              isOpen={this.state.confirmationModalIsOpen}
              contentLabel="Confirm Disable Supplier"
              onRequestClose={this.closeConfirmationModal}
            >
              <div>
                <h2 className="supplier-modal-message">Disable supplier?</h2>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Button onClick={this.closeConfirmationModal}>Cancel</Button>
                <Button onClick={this.delete} style={{ marginLeft: 10 }}>Disable</Button>
              </div>
            </Modal>
            <Modal
              isOpen={this.state.infoModalIsOpen}
              contentLabel="Info"
              onRequestClose={this.closeInfoModal}
            >
              <div>
                <h2 className="supplier-modal-message">{this.state.infoModalText}</h2>
              </div>
            </Modal>
          </div>
        }
      </div>
    );
  }
}

function mapStateToProps({ suppliers, user }) {
  const { suppliersData, orgKey, supplierKey, isLoading } = suppliers;
  const supplierData = suppliersData && suppliersData[supplierKey] ? suppliers.suppliersData[supplierKey] : {};
  const { userId } = user;
  return {
    orgKey,
    supplierKey,
    supplierData,
    userId,
    isLoading,
  };
}

export default connect(mapStateToProps)(SupplierBasicInformationContainer);
